import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking", orderId: null });
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing", orderId: null });
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState({ status: "paid", orderId: r.data.order_id });
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempts.current < 15) {
        setTimeout(poll, 2000);
      } else {
        setState({ status: "timeout", orderId: null });
      }
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return (
    <main data-testid="payment-success-page" className="flex min-h-screen flex-col items-start justify-center px-6 pt-24 sm:px-10">
      {state.status === "checking" && (
        <div data-testid="payment-checking" className="flex items-center gap-4">
          <Loader2 className="animate-spin text-taupe" size={28} />
          <p className="font-serif text-3xl font-light">Confirming your payment...</p>
        </div>
      )}
      {state.status === "paid" && (
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <span className="mb-8 flex h-16 w-16 items-center justify-center rounded-full bg-moss text-bone">
            <Check size={26} />
          </span>
          <h1 className="font-serif text-4xl font-light tracking-tight sm:text-6xl">Thank you.</h1>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-ash">
            Your order <span data-testid="order-id" className="font-medium text-charcoal">{state.orderId}</span> is
            confirmed and paid. We'll be in touch to arrange a hand-delivery time that suits you.
          </p>
          <Link
            to="/shop"
            data-testid="success-continue-shopping"
            className="mt-12 inline-flex rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss"
          >
            Continue browsing
          </Link>
        </motion.div>
      )}
      {(state.status === "timeout" || state.status === "missing") && (
        <div data-testid="payment-unconfirmed">
          <h1 className="font-serif text-4xl font-light tracking-tight sm:text-5xl">We're still checking.</h1>
          <p className="mt-6 max-w-md text-base font-light leading-relaxed text-ash">
            Your payment hasn't confirmed yet — if the amount was charged, your order is safe and will appear
            shortly. Nothing is charged twice.
          </p>
          <Link to="/" data-testid="success-back-home" className="mt-12 inline-flex rounded-full border border-charcoal/20 px-8 py-4 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-charcoal hover:text-bone">
            Back home
          </Link>
        </div>
      )}
    </main>
  );
}
